import * as React from 'react'
import {
  Alert,
  Button,
  CONTRAST_BUTTON,
  ERROR_ALERT_CONTEXTUAL,
  FormElementWrapper,
  getAlertUniqueId,
  Input,
  SIZE_SMALL,
} from '@mds/mds-reactjs-library'
import './Login.css'
import FirmMemberLoginSection from '../components/FirmMemberLoginSection'
import ForgotPasswordLink from '../components/ForgotPasswordLink'
import PasswordWithToggle from '../components/PasswordWithToggle'
import TwoPanelViewLayout from '../components/TwoPanelViewLayout'
import { classes } from '../components/ViewLayout'
import type { AuthState } from '../types/AuthState'
import type { Customization } from '../types/Customization'
import { emailRegex, ERROR_NO_ACCESS } from '../util/constants'
import { kcAlertMessage } from '../util/kcMessage'
import { KcError } from '../util/KeycloakError'
import { hostedLogin, webfingerDiscovery } from '../util/loginActions'

type LoginProps = {
  jwt: string
  error: string
  kcMessage: string
  authState: AuthState
  onSessionTimeout?: () => void
  customization?: Customization
}

type ValidationType = {
  isError: boolean
  label: string
}

const Login = ({ jwt, error, authState, kcMessage, onSessionTimeout, customization }: LoginProps): JSX.Element => {
  const loginHint = authState?.assetNotes?.login_hint
  const [email, setEmail] = React.useState<string>(loginHint || '')
  const [showPassword, setShowPassword] = React.useState(false)
  const [password, setPassword] = React.useState<string>('')
  const [emailComplete, setEmailComplete] = React.useState<boolean>(false)
  const [emailValid, setEmailValid] = React.useState<boolean>(false)
  const [kcErrorMessage, setKcErrorMessage] = React.useState<string>()

  const keycloakErrorHandler = (kcError: KcError | Error | any) => {
    if (kcError instanceof KcError) {
      setKcErrorMessage(kcError.message)
    } else {
      let message = ''
      if (kcError instanceof Error) message = `${kcError.name}: ${kcError.message}`
      else if (typeof kcError === 'string') message = kcError
      setKcErrorMessage(message)
    }
  }

  const continueLogin = async (): Promise<void> => {
    if (emailComplete) {
      try {
        await hostedLogin({ authState, jwt, email, password })
      } catch (e) {
        keycloakErrorHandler(e)
      }
    } else {
      try {
        const flow = await webfingerDiscovery({ authState, jwt, email })
        /**
         * @NOTE
         * Sometimes, before the user is fully redirected, the password field is rendered on screen
         * This here is to prevent it from rendering before redirecting
         */
        if (flow !== 'federated') {
          setEmailComplete(true)
        }
      } catch (e) {
        keycloakErrorHandler(e)
      }
    }
  }

  const editEmail = (value: string): void => {
    if (emailComplete) {
      setEmailComplete(false)
      setPassword('')
    }
    setEmail(value)
    setKcErrorMessage(undefined)
  }

  const validateEmail = (value: string): ValidationType => {
    const isValid = emailRegex.test(value)
    setEmailValid(isValid)
    return {
      isError: !isValid,
      label: isValid ? '' : 'This is not a valid email address',
    }
  }

  const editPassword = (value: string): void => {
    setPassword(value)
    setKcErrorMessage(undefined)
  }

  const onKeyUp = async (e: React.KeyboardEvent<HTMLInputElement>): Promise<void> => {
    if (!e.currentTarget.value) return
    if (e.key === 'Enter') {
      e.preventDefault()
      await continueLogin()
    }
  }

  const expireAt = authState.exp * 1_000
  let timeout: ReturnType<typeof setTimeout>
  const checkExpiration = (reschedule = false): void => {
    // handle invalid authState as not an expiration
    if (!authState.exp) {
      clearTimeout(timeout)
    }

    // if not expired, either ignore or reschedule for 1 second
    const now = Date.now()
    if (now <= expireAt) {
      if (reschedule) {
        clearTimeout(timeout)
        timeout = setTimeout(() => checkExpiration(true), 1_000)
      }
      return
    }

    onSessionTimeout?.()
    clearTimeout(timeout)
  }

  React.useEffect(() => {
    timeout = setTimeout(() => checkExpiration(true), expireAt - Date.now())
    checkExpiration()
    return (): void => {
      clearTimeout(timeout)
    }
  }, [authState.exp])

  // Error handler
  React.useEffect(() => {
    if (!error) return

    let text: string | undefined
    if (error === 'K00') text = kcAlertMessage(kcMessage)
    else if (error === 'K02') text = ERROR_NO_ACCESS

    /** @NOTE if we have an error, but none of the above, should we have a default handler? */
    if (!text) return

    setKcErrorMessage(text)
  }, [error])

  React.useEffect(() => {
    if (loginHint && email && loginHint === email) {
      validateEmail(email)
      editEmail(email)
    }
  }, [])

  const handlePasswordToggle = React.useCallback((toggleFn: React.Dispatch<React.SetStateAction<boolean>>) => {
    toggleFn((prev) => !prev)
  }, [])

  const brandedTheme = Boolean(customization?.buttonBackgroundColor || customization?.buttonTextColor)
  const pageStyle: React.CSSProperties | undefined = brandedTheme
    ? ({
        ...(customization?.buttonBackgroundColor
          ? {
              ['--login-cta-bg']: customization.buttonBackgroundColor,
              ['--login-link-color']: customization.buttonBackgroundColor,
            }
          : {}),
        ...(customization?.buttonTextColor ? { ['--login-cta-text']: customization.buttonTextColor } : {}),
      } as React.CSSProperties)
    : undefined
  const pageClassName = ['login-page', brandedTheme ? 'login-page--branded' : null].filter(Boolean).join(' ')

  const primaryCtaDisabled = !!kcErrorMessage || !email || !emailValid || (emailComplete && !password)

  const primaryCtaButton = (
    <Button
      type="submit"
      onClick={continueLogin}
      className={classes.ctaButton}
      appearance={CONTRAST_BUTTON}
      size={SIZE_SMALL}
      disabled={primaryCtaDisabled}
      style={
        brandedTheme
          ? {
              backgroundColor: customization?.buttonBackgroundColor,
              color: customization?.buttonTextColor,
            }
          : undefined
      }
    >
      {emailComplete ? 'Continue' : 'Continue with email'}
    </Button>
  )

  return (
    <TwoPanelViewLayout
      className={pageClassName}
      style={pageStyle}
      title="Sign in"
      subtitle="Sign in to your McKinsey account"
    >
      <div className="loginCredentials">
        <div className="loginCredentials__inputWrapper">
          <FormElementWrapper
            component={Input}
            label="Email"
            inputProps={{
              autoFocus: true,
              type: 'email',
              name: 'username',
              placeholder: '',
              value: email,
              onKeyUp: (e: React.KeyboardEvent<HTMLInputElement>): Promise<void> => onKeyUp(e),
              onChange: (e: React.ChangeEvent<HTMLInputElement>): void => editEmail(e.currentTarget.value),
            }}
            validationFunc={validateEmail}
          />
        </div>
        {emailComplete && (
          <>
            <div className="loginCredentials__inputWrapper">
              <FormElementWrapper
                component={PasswordWithToggle}
                label="Password"
                inputProps={{
                  autoFocus: true,
                  placeholder: 'Enter your password',
                  name: 'password',
                  value: password,
                  type: showPassword ? 'text' : 'password',
                  onKeyUp: (e: React.KeyboardEvent<HTMLInputElement>): Promise<void> => onKeyUp(e),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>): void => editPassword(e.currentTarget.value),
                  buttonProps: {
                    value: password,
                    showPassword,
                    onToggle: () => handlePasswordToggle(setShowPassword),
                  },
                }}
              />
              <ForgotPasswordLink authState={authState} email={email} color={customization?.buttonBackgroundColor} />
            </div>
          </>
        )}
        {kcErrorMessage && (
          <div className="loginCredentials__alertWrapper">
            <Alert
              id={getAlertUniqueId()}
              type={ERROR_ALERT_CONTEXTUAL}
              showCloseIcon={false}
              className="loginCredentials__contextualAlert"
              style={{
                minWidth: 0,
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}
            >
              {kcErrorMessage}
            </Alert>
          </div>
        )}
        {!emailComplete ? (
          <div className="loginCredentials__alternateAuthActions">
            {primaryCtaButton}
            <FirmMemberLoginSection authState={authState} jwt={jwt} />
          </div>
        ) : (
          primaryCtaButton
        )}
      </div>
    </TwoPanelViewLayout>
  )
}

export default Login
